import { MeshBuilder, StandardMaterial, Color3, Vector3, Ray, Animation, CubicEase, EasingFunction, ParticleSystem, Texture, Color4, SceneLoader, DynamicTexture } from "@babylonjs/core";
import { logger } from "../core/Logger.js";

/**
 * @class Player
 * @description Représente le joueur (Yasso). Gère les mouvements, le dash, la vie et les bonus.
 */
export class Player {
    /**
     * @param {import("../core/GameManager.js").GameContext} context
     */
    constructor(context) {
        /** @type {import("../core/GameManager.js").GameContext} */
        this.context = context;
        /** @type {import("@babylonjs/core").Scene} */
        this.scene = context.scene;

        /** @type {import("@babylonjs/core").Mesh} */
        this.mesh = null;
        /** @type {import("@babylonjs/core").Mesh} */
        this.trailMesh = null;

        this._initMesh();

        // ─────────────────────────────
        // GAMEPLAY NERVEUX (STYLE DIABLO/HADES)
        // ─────────────────────────────
        /** @type {number} */
        this.baseSpeed = 0.24;
        /** @type {number} */
        this.maxSpeed = 0.36;
        /** @type {number} */
        this.speedAcceleration = 0.005;
        /** @type {number} */
        this.speed = this.baseSpeed;
        /** @type {number} */
        this.attackPower = 1;
        /** @type {import("@babylonjs/core").Vector3} */
        this.lastMoveDirection = new Vector3(0, 0, 1);

        // Dash
        /** @type {boolean} */
        this.isDashing = false;
        /** @type {boolean} */
        this.isDashReady = true;
        /** @type {number} */
        this.dashCooldown = 280;
        /** @type {number} */
        this.dashDistance = 5.5;
        /** @type {number} */
        this.dashDuration = 9;

        /** @type {import("@babylonjs/core").Animatable|null} */
        this.currentDashAnim = null;

        // Système de vie
        /** @type {number} */
        this.maxHealth = 80;
        /** @type {number} HP max de base (avant augmentations de run) */
        this._baseMaxHealth = 80;
        /** @type {number} */
        this.currentHealth = this.maxHealth;
        /** @type {boolean} */
        this.isInvincible = false;
        /** @type {number} */
        this.invincibilityDuration = 700;

        // Bonus
        /** @type {string|null} */
        this.activePower = null;
        /** @type {string|null} */
        this.storedPower = null;
        /** @type {number} */
        this.powerTimer = 0;

        // Sprint
        /** @type {boolean} */
        this.isSprinting = false;
        /** @type {number} */
        this.sprintMultiplier = 1.4;

        // Slide (dash au sol pendant le sprint)
        /** @type {boolean} */
        this.isSliding = false;
        /** @type {boolean} */
        this.isSlideReady = true;
        /** @type {number} */
        this.slideCooldown = 700;
        /** @type {number} */
        this.slideDistance = 7.5;
        /** @type {number} */
        this.slideDuration = 20;

        // Saut
        /** @type {boolean} */
        this.isJumping = false;
        /** @type {boolean} */
        this.isJumpReady = true;
        /** @type {number} */
        this.jumpCooldown = 420;
        /** @type {number} */
        this.jumpHeight = 2.5;
        /** @type {number} */
        this._jumpFrame = 0;
        /** @type {number} */
        this._jumpTotalFrames = 40;
        /** @type {number} */
        this._jumpOffset = 0;
        /** @type {number} */
        this._groundY = 0.5;
        /** @type {boolean} @private */
        this._apexInvincible = false;
        /** @type {import("@babylonjs/core").Mesh|null} @private */
        this._jumpShadow = null;

        // Anti double-hit : ennemis récemment touchés (cooldown par ennemi)
        /** @type {Set<import("./Enemy.js").Enemy>} */
        this._recentlyHitEnemies = new Set();

        /** @type {NodeJS.Timeout|null} @private */
        this._invincibilityInterval = null;
    }

    /**
     * Initialise le mesh du joueur et ses matériaux
     * @private
     */
    _initMesh() {
        // Hitbox
        this.mesh = MeshBuilder.CreateBox("yasso_hitbox", { width: 0.85, height: 0.85, depth: 0.85 }, this.scene);
        this.mesh.position.y = 0.5;
        this.mesh.isVisible = false;

        // Visual container child of hitbox
        const visualGroup = MeshBuilder.CreateBox("yasso_visual_group", { size: 0.01 }, this.scene);
        visualGroup.parent = this.mesh;
        visualGroup.position.y = 0.1; // Centered vertically in the hitbox
        visualGroup.isVisible = false;
        this.visualGroup = visualGroup;

        // Core: Neon Cyan octahedron (type 1 is octahedron in MeshBuilder)
        const core = MeshBuilder.CreatePolyhedron("yasso_core", { type: 1, size: 0.32 }, this.scene);
        core.parent = visualGroup;
        core.position = Vector3.Zero();

        const coreMat = new StandardMaterial("yassoCoreMat", this.scene);
        coreMat.diffuseColor = new Color3(0.0, 0.05, 0.1);
        coreMat.emissiveColor = new Color3(0.0, 0.8, 1.0); // Neon Cyan
        coreMat.specularColor = new Color3(0.0, 0.8, 1.0);
        coreMat.alpha = 0.95;
        core.material = coreMat;
        core.isPickable = false;

        // Ring 1: Neon Magenta thin torus rotating around core
        const ring1 = MeshBuilder.CreateTorus("yasso_ring1", { diameter: 0.8, thickness: 0.035, tessellation: 24 }, this.scene);
        ring1.parent = visualGroup;
        ring1.rotation.x = Math.PI / 4;

        const ring1Mat = new StandardMaterial("yassoRing1Mat", this.scene);
        ring1Mat.diffuseColor = new Color3(0.05, 0.0, 0.05);
        ring1Mat.emissiveColor = new Color3(1.0, 0.0, 0.8); // Neon Magenta/Pink
        ring1Mat.specularColor = new Color3(1.0, 0.0, 0.8);
        ring1.material = ring1Mat;
        ring1.isPickable = false;

        // Ring 2: Neon Purple thin torus rotating at an opposite angle
        const ring2 = MeshBuilder.CreateTorus("yasso_ring2", { diameter: 1.0, thickness: 0.025, tessellation: 24 }, this.scene);
        ring2.parent = visualGroup;
        ring2.rotation.z = -Math.PI / 4;

        const ring2Mat = new StandardMaterial("yassoRing2Mat", this.scene);
        ring2Mat.diffuseColor = new Color3(0.02, 0.0, 0.05);
        ring2Mat.emissiveColor = new Color3(0.6, 0.0, 1.0); // Neon Violet
        ring2Mat.specularColor = new Color3(0.6, 0.0, 1.0);
        ring2.material = ring2Mat;
        ring2.isPickable = false;

        this._ring1 = ring1;
        this._ring2 = ring2;
        this._core = core;

        this._createDashTrail();
        this._createJumpShadow();
    }

    /**
     * Crée l'ombre au sol pour le saut
     * @private
     */
    _createJumpShadow() {
        this._jumpShadow = MeshBuilder.CreateDisc("jumpShadow", { radius: 0.45, tessellation: 16 }, this.scene);
        this._jumpShadow.rotation.x = Math.PI / 2;
        this._jumpShadow.position.y = 0.02;
        this._jumpShadow.isPickable = false;
        this._jumpShadow.isVisible = false;

        const shadowMat = new StandardMaterial("jumpShadowMat", this.scene);
        shadowMat.diffuseColor = new Color3(0, 0, 0);
        shadowMat.emissiveColor = new Color3(0, 0, 0);
        shadowMat.alpha = 0.5;
        shadowMat.backFaceCulling = false;
        this._jumpShadow.material = shadowMat;
    }

    /**
     * Crée le mesh de traînée pour le dash
     * @private
     */
    _createDashTrail() {
        this.trailMesh = MeshBuilder.CreateSphere("trail", { diameter: 0.9, segments: 12 }, this.scene);
        this.trailMesh.parent = this.mesh;
        this.trailMesh.position = new Vector3(0, 0, 0);

        const trailMat = new StandardMaterial("trailMat", this.scene);
        trailMat.emissiveColor = new Color3(0.8, 0.0, 0.4); // Magenta sanglant
        trailMat.alpha = 0;
        this.trailMesh.material = trailMat;
        this.trailMesh.isPickable = false;
    }

    /**
     * Change la transparence du joueur
     * @param {number} alpha
     * @private
     */
    _setAlpha(alpha) {
        if (this.mesh && this.mesh.material) {
            this.mesh.material.alpha = alpha;
        }
        if (this.mesh) {
            this.mesh.getChildMeshes().forEach(m => {
                if (m.material) {
                    m.material.alpha = alpha;
                    if (m.material.transparencyMode !== undefined) {
                        m.material.transparencyMode = alpha < 1 ? 2 : 0; // 2: ALPHABLEND, 0: OPAQUE
                    }
                }
            });
        }
    }

    /**
     * Réinitialise l'état complet du joueur (santé, pouvoirs, etc.)
     */
    reset() {
        this.resetMovement();
        this.maxHealth = this._baseMaxHealth; // Reset les HP max entre les runs
        this.currentHealth = this.maxHealth;
        this.isInvincible = false;
        this.activePower = null;
        this.storedPower = null;
        this.powerTimer = 0;
        this.speed = this.baseSpeed;
        this._setAlpha(0.8);
        this._setEmissiveColor(new Color3(0, 1, 1));
    }

    /**
     * Réinitialise uniquement les états de mouvement (utile pour changement de salle)
     */
    resetMovement() {
        this.cancelDash();

        if (this.mesh) {
            this.mesh.rotation = Vector3.Zero();
        }
        this.lastMoveDirection = new Vector3(0, 0, 1);

        this.isDashing = false;
        this.isDashReady = true;
        this.isSliding = false;
        this.isSlideReady = true;
        this.isSprinting = false;
        this.isJumping = false;
        this.isJumpReady = true;
        this._jumpOffset = 0;
        this._jumpFrame = 0;
        this._apexInvincible = false;
        if (this._jumpShadow) this._jumpShadow.isVisible = false;

        logger.info("ENTITY", "DEBUG", "🔄 Player movement reset (sans repositionnement)");
    }

    /**
     * Annule le dash en cours
     */
    cancelDash() {
        if (this.currentDashAnim) {
            this.currentDashAnim.stop();
            this.currentDashAnim = null;
        }
        this.isDashing = false;
        this._hideDashTrail();
    }

    /**
     * Vérifie si une position de mouvement est valide (présence de sol)
     * @param {import("@babylonjs/core").Vector3} targetPosition
     * @param {import("../logic/LevelManager.js").LevelManager} levelManager
     * @returns {boolean}
     * @private
     */
    _isValidMove(targetPosition, levelManager) {
        // ✅ OPTIMISATION: Utilisation du lookup Set O(1) de la Room
        if (levelManager && levelManager.currentRoom) {
            return levelManager.currentRoom.isValidPosition(targetPosition.x, targetPosition.z);
        }

        // Fallback Raycast si LevelManager non dispo (ne devrait pas arriver in-game)
        const rayOriginY = this.mesh.position.y + 4;
        const origin = new Vector3(targetPosition.x, rayOriginY, targetPosition.z);
        const direction = new Vector3(0, -1, 0);
        const ray = new Ray(origin, direction, 6.5);

        const hitInfo = this.scene.pickWithRay(ray, (mesh) => {
            return mesh.name === "p" || mesh.name === "exit" || mesh.name.includes("portal");
        });

        return hitInfo.hit;
    }

    /**
     * Mise à jour logique à chaque frame
     * @param {import("../logic/InputManager.js").InputManager} inputManager
     * @param {import("../logic/ai/DataCollector.js").DataCollector} aiCollector
     * @param {import("../logic/LevelManager.js").LevelManager} levelManager
     */
    update(inputManager, aiCollector, levelManager) {
        // Animate visual components
        if (this.visualGroup) {
            this.visualGroup.position.y = 0.1 + Math.sin(Date.now() * 0.005) * 0.12;
            this.visualGroup.rotation.y += 0.025;
        }
        if (this._ring1) {
            this._ring1.rotation.x += 0.02;
            this._ring1.rotation.y += 0.01;
        }
        if (this._ring2) {
            this._ring2.rotation.z -= 0.015;
            this._ring2.rotation.y += 0.03;
        }

        // Timer des pouvoirs
        if (this.activePower) {
            this.powerTimer--;
            if (this.powerTimer <= 0) {
                this.deactivatePower();
            }
        }

        // Activation du pouvoir stocké
        if (inputManager.isInteractTriggered() && this.storedPower) {
            this.activatePower(this.storedPower);
            this.storedPower = null;
        }

        if (this.isDashing) return;

        let moveDir = Vector3.Zero();
        const input = inputManager.getMovementInput();

        // Enregistrement IA (Intention du joueur)
        if (input.z > 0) aiCollector.recordMove("up");
        if (input.z < 0) aiCollector.recordMove("down");
        if (input.x < 0) aiCollector.recordMove("left");
        if (input.x > 0) aiCollector.recordMove("right");

        // Calcul du vecteur de mouvement
        if (Math.abs(input.x) > 0 || Math.abs(input.z) > 0) {

            // ✅ CORRECTION : Mouvement relatif à la caméra
            if (this.scene.activeCamera) {
                if (!this._camForward) {
                    this._camForward = new Vector3(0, 0, 0);
                    this._camRight = new Vector3(0, 0, 0);
                    this._moveDirResult = new Vector3(0, 0, 0);
                }
                const dir = this.scene.activeCamera.getForwardRay().direction;
                this._camForward.copyFrom(dir);
                this._camForward.y = 0;
                this._camForward.normalize();

                Vector3.CrossToRef(Vector3.Up(), this._camForward, this._camRight);

                this._camForward.scaleInPlace(input.z);
                this._camRight.scaleInPlace(input.x);
                this._moveDirResult.copyFrom(this._camForward).addInPlace(this._camRight);

                moveDir = this._moveDirResult;
            } else {
                // Fallback (axes du monde)
                if (!this._fallbackMove) this._fallbackMove = new Vector3(0, 0, 0);
                moveDir = this._fallbackMove.copyFromFloats(input.x, 0, input.z);
            }

            if (moveDir.length() > 0) {
                moveDir.normalize();
                this.lastMoveDirection = moveDir.clone();

                this.speed = Math.min(this.speed + this.speedAcceleration, this.maxSpeed);
                const nextPos = this.mesh.position.add(moveDir.scale(this.speed));
                nextPos.y = this.mesh.position.y; // Le saut gère Y indépendamment

                // ✅ OPTIMISATION: Passage du levelManager pour check O(1)
                if (this._isValidMove(nextPos, levelManager)) {
                    this.mesh.position.x = nextPos.x;
                    this.mesh.position.z = nextPos.z;
                }

                this.mesh.rotation.y = Math.atan2(moveDir.x, moveDir.z);
            }
        }

        this.isSprinting = false;

        if (moveDir.length() === 0) {
            this.speed = this.baseSpeed;
        }

        // Mise à jour du saut (arc parabolique, chaque frame)
        this._updateJump();

        // Déclencheur saut (Maj)
        if (inputManager.isJumpTriggered?.() && this.isJumpReady && !this.isJumping) {
            this._executeJump();
        }

        // Déclencheur dash / slide (ESPACE)
        if (inputManager.isDashTriggered() && this.isDashReady && !this.isJumping) {
            const dashDir = moveDir.length() > 0 ? moveDir : this.lastMoveDirection;
            if (this.isSprinting && this.isSlideReady) {
                this._executeSlide(dashDir, levelManager);
            } else {
                this.executeDash(dashDir, aiCollector, levelManager);
            }
        }
    }

    /**
     * Inflige des dégâts au joueur
     * @returns {boolean} True si le joueur est mort
     */
    takeDamage() {
        if (this.isInvincible || (this.activePower === "Sentinelle")) return null;

        this.currentHealth--;
        this.speed = this.baseSpeed;
        logger.info("ENTITY", "DEBUG", `YASSO HIT! Health: ${this.currentHealth}/${this.maxHealth}`);

        if (this.currentHealth <= 0) {
            logger.info("ENTITY", "DEBUG", "YASSO DESTROYED");
            this.isInvincible = true;
            return true;
        }

        if (this.context.vfx) this.context.vfx.playerHit();
        this._activateInvincibility();
        return false;
    }

    /**
     * Active l'invincibilité temporaire et l'effet visuel de clignotement
     * @private
     */
    _activateInvincibility() {
        this.isInvincible = true;

        // Annuler un éventuel intervalle précédent (double-hit)
        if (this._invincibilityInterval) {
            clearInterval(this._invincibilityInterval);
            this._invincibilityInterval = null;
        }

        let blinkCount = 0;
        let isBlinkOff = false;
        this._invincibilityInterval = setInterval(() => {
            isBlinkOff = !isBlinkOff;
            this._setAlpha(isBlinkOff ? 0.3 : 0.8);
            blinkCount++;

            if (blinkCount >= 10) {
                if (this._invincibilityInterval) {
                    clearInterval(this._invincibilityInterval);
                    this._invincibilityInterval = null;
                }
                this._setAlpha(0.8);
                this.isInvincible = false;
                logger.info("ENTITY", "DEBUG", "Invincibility ended");
            }
        }, this.invincibilityDuration / 10);
    }

    /**
     * Exécute un dash dans une direction donnée
     * @param {import("@babylonjs/core").Vector3} direction
     * @param {import("../logic/ai/DataCollector.js").DataCollector} aiCollector
     * @param {import("../logic/LevelManager.js").LevelManager} levelManager
     */
    executeDash(direction, aiCollector, levelManager) {
        if (!this.isDashReady || this.isDashing) return;

        this.isDashReady = false;
        this.isDashing = true;
        this._recentlyHitEnemies.clear(); // Nouveau dash → reset des cooldowns de hit
        aiCollector.recordDash();

        if (this.context.audioManager) this.context.audioManager.playSound("dash");

        if (this.activePower === "Pulse") {
            this._triggerPulseExplosion();
        }

        const dashDir = direction.normalize();

        this._showDashTrail();
        if (this.context.vfx) this.context.vfx.dashBurst(this.mesh.position.clone(), dashDir);

        let targetPos = this.mesh.position.add(dashDir.scale(this.dashDistance));
        if (!this._isValidMove(targetPos, levelManager)) {
            // Tentative de dash plus court si mur
            targetPos = this.mesh.position.add(dashDir.scale(this.dashDistance * 0.5));
            if (!this._isValidMove(targetPos, levelManager)) {
                targetPos = this.mesh.position.clone();
            }
        }

        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);

        this.currentDashAnim = Animation.CreateAndStartAnimation(
            "dashAnim",
            this.mesh,
            "position",
            60,
            this.dashDuration, // Plus rapide
            this.mesh.position,
            targetPos,
            Animation.ANIMATIONLOOPMODE_CONSTANT,
            ease,
            () => {
                this.isDashing = false;
                this._hideDashTrail();
                this.currentDashAnim = null;
            }
        );

        setTimeout(() => { this.isDashReady = true; }, this.dashCooldown);
    }

    /**
     * Exécute un saut (arc parabolique, invincibilité à l'apex)
     * @private
     */
    _executeJump() {
        if (!this.isJumpReady || this.isJumping) return;

        this.isJumpReady = false;
        this.isJumping = true;
        this._jumpFrame = 0;
        this._jumpOffset = 0;

        if (this.context.audioManager) this.context.audioManager.playSound("dash");

        setTimeout(() => { this.isJumpReady = true; }, this.jumpCooldown);
    }

    /**
     * Met à jour l'arc de saut frame par frame et l'ombre au sol
     * @private
     */
    _updateJump() {
        if (this.isJumping) {
            this._jumpFrame++;
            const t = this._jumpFrame / this._jumpTotalFrames;
            // Parabole : monte puis redescend
            this._jumpOffset = 4 * this.jumpHeight * t * (1 - t);

            // Invincibilité à l'apex (frames 40%–60% de l'arc)
            const isApex = t >= 0.38 && t <= 0.62;
            if (isApex && !this._apexInvincible && !this.isInvincible) {
                this._apexInvincible = true;
                this.isInvincible = true;
            } else if (!isApex && this._apexInvincible) {
                this._apexInvincible = false;
                this.isInvincible = false;
            }

            if (this._jumpFrame >= this._jumpTotalFrames) {
                this.isJumping = false;
                this._jumpOffset = 0;
                if (this._apexInvincible) {
                    this._apexInvincible = false;
                    this.isInvincible = false;
                }
            }
        }

        // Appliquer le décalage vertical au mesh (sans toucher au dash)
        if (this.mesh) {
            this.mesh.position.y = this._groundY + this._jumpOffset;
        }

        // Ombre au sol : suit le joueur et s'atténue à l'apex
        if (this._jumpShadow && this.mesh) {
            if (this.isJumping) {
                this._jumpShadow.position.x = this.mesh.position.x;
                this._jumpShadow.position.z = this.mesh.position.z;
                this._jumpShadow.isVisible = true;
                const progress = this._jumpOffset / this.jumpHeight; // 0=sol, 1=apex
                const scale = 0.5 + (1 - progress) * 0.5;
                this._jumpShadow.scaling.x = scale;
                this._jumpShadow.scaling.z = scale;
                this._jumpShadow.material.alpha = 0.5 * (1 - progress * 0.65);
            } else {
                this._jumpShadow.isVisible = false;
            }
        }
    }

    /**
     * Exécute un slide (dash au sol, plus long, invincibilité)
     * @param {import("@babylonjs/core").Vector3} direction
     * @param {import("../logic/LevelManager.js").LevelManager} levelManager
     * @private
     */
    _executeSlide(direction, levelManager) {
        if (!this.isSlideReady || this.isDashing) return;

        this.isSlideReady = false;
        this.isDashReady = false;
        this.isDashing = true;
        this.isSliding = true;
        this._recentlyHitEnemies.clear();

        // Incliner le joueur vers l'avant pour l'effet de glissade
        this.mesh.rotation.x = Math.PI / 4.5;

        this._showSlideTrail();

        // Invincibilité pendant le slide
        if (!this.isInvincible) {
            this.isInvincible = true;
            setTimeout(() => { this.isInvincible = false; }, 380);
        }

        const slideDir = direction.normalize();
        let targetPos = this.mesh.position.add(slideDir.scale(this.slideDistance));
        targetPos.y = this._groundY;
        if (!this._isValidMove(targetPos, levelManager)) {
            targetPos = this.mesh.position.add(slideDir.scale(this.slideDistance * 0.5));
            targetPos.y = this._groundY;
            if (!this._isValidMove(targetPos, levelManager)) {
                targetPos = this.mesh.position.clone();
                targetPos.y = this._groundY;
            }
        }

        const ease = new CubicEase();
        ease.setEasingMode(EasingFunction.EASINGMODE_EASEOUT);

        this.currentDashAnim = Animation.CreateAndStartAnimation(
            "slideAnim", this.mesh, "position",
            60, this.slideDuration,
            this.mesh.position.clone(), targetPos,
            Animation.ANIMATIONLOOPMODE_CONSTANT,
            ease,
            () => {
                this.isDashing = false;
                this.isSliding = false;
                this.mesh.rotation.x = 0;
                this._hideDashTrail();
                this._restoreTrailColor();
                this.currentDashAnim = null;
            }
        );

        if (this.context.audioManager) this.context.audioManager.playSound("dash");

        setTimeout(() => {
            this.isSlideReady = true;
            this.isDashReady = true;
        }, this.slideCooldown);
    }

    /**
     * Affiche la traînée visuelle du slide (cyan)
     * @private
     */
    _showSlideTrail() {
        if (!this.trailMesh) return;
        const trailMat = this.trailMesh.material;
        trailMat.emissiveColor = new Color3(0.0, 0.9, 1.0); // Cyan pour le slide
        trailMat.alpha = 0.75;

        Animation.CreateAndStartAnimation(
            "slideTrailFade", trailMat, "alpha",
            60, 28, 0.75, 0.0,
            Animation.ANIMATIONLOOPMODE_CONSTANT
        );
    }

    /**
     * Restaure la couleur de la traînée (magenta pour le dash)
     * @private
     */
    _restoreTrailColor() {
        if (!this.trailMesh || !this.trailMesh.material) return;
        this.trailMesh.material.emissiveColor = new Color3(0.8, 0.0, 0.4);
    }

    /**
     * Affiche la traînée visuelle du dash
     * @private
     */
    _showDashTrail() {
        if (!this.trailMesh) return;
        const trailMat = this.trailMesh.material;

        // Stopper toute animation en cours sur le matériau avant d'en lancer une nouvelle
        this.scene.stopAnimation(trailMat);
        trailMat.alpha = 0.6;

        Animation.CreateAndStartAnimation(
            "trailPulse",
            trailMat,
            "alpha",
            60,
            20,
            0.6,
            0.1,
            Animation.ANIMATIONLOOPMODE_CONSTANT
        );
    }

    /**
     * Masque la traînée visuelle
     * @private
     */
    _hideDashTrail() {
        if (!this.trailMesh) return;
        this.trailMesh.material.alpha = 0;
    }

    /**
     * Collecte un pouvoir (le stocke en attente d'activation)
     * @param {string} type
     */
    collectPower(type) {
        this.storedPower = type;
        logger.info("ENTITY", "DEBUG", `POWER STORED: ${type}`);
    }

    /**
     * Change la couleur émissive du joueur
     * @param {import("@babylonjs/core").Color3} color
     * @private
     */
    _setEmissiveColor(color) {
        if (this.mesh.material) {
            this.mesh.material.emissiveColor = color;
        }
        this.mesh.getChildMeshes().forEach(m => {
            if (m.material && m.material.emissiveColor !== undefined) {
                m.material.emissiveColor = color;
            }
        });
    }

    /**
     * Active un pouvoir immédiatement
     * @param {string} type
     */
    activatePower(type) {
        this.deactivatePower();
        this.activePower = type;
        this.powerTimer = 600;

        logger.info("ENTITY", "DEBUG", `POWER UP ACTIVATED: ${type}`);

        if (type === "Force") {
            this.attackPower = 2;
            this._setEmissiveColor(new Color3(1.0, 0.35, 0.0));
            this._showPowerFlash("brightness(1.8) hue-rotate(-30deg)");
        } else if (type === "Vitesse") {
            this.maxSpeed = 0.50;
            this._setEmissiveColor(new Color3(0.0, 0.7, 1.0));
            this._showPowerFlash("brightness(1.8) hue-rotate(170deg)");
        }

        if (this.context.audioManager) this.context.audioManager.playSound("bonus");
    }

    /**
     * Désactive le pouvoir actif
     */
    deactivatePower() {
        if (!this.activePower) return;

        logger.info("ENTITY", "DEBUG", "POWER UP ENDED");
        this.activePower = null;
        this.powerTimer = 0;
        this.attackPower = 1;
        this.maxSpeed = 0.36;
        this.speed = Math.min(this.speed, this.maxSpeed);
        this._setEmissiveColor(new Color3(0, 1, 1));
    }

    /**
     * Déclenche l'effet visuel et sonore de l'explosion du pouvoir Pulse
     * @private
     */
    _triggerPulseExplosion() {
        const particleSystem = new ParticleSystem("pulseExplosion", 50, this.scene);

        // Procedural flare texture (avoids HTTP request/DNS failures)
        const flareSize = 64;
        const dynamicTexture = new DynamicTexture("proceduralFlare", flareSize, this.scene, false);
        const context = dynamicTexture.getContext();
        context.clearRect(0, 0, flareSize, flareSize);
        const center = flareSize / 2;
        const gradient = context.createRadialGradient(center, center, 0, center, center, flareSize / 2);
        gradient.addColorStop(0, "rgba(255, 255, 255, 1.0)");
        gradient.addColorStop(0.2, "rgba(240, 240, 240, 0.8)");
        gradient.addColorStop(0.5, "rgba(200, 200, 200, 0.3)");
        gradient.addColorStop(1.0, "rgba(0, 0, 0, 0.0)");
        context.fillStyle = gradient;
        context.fillRect(0, 0, flareSize, flareSize);
        dynamicTexture.update();

        particleSystem.particleTexture = dynamicTexture;
        particleSystem.emitter = this.mesh.position.clone();
        particleSystem.color1 = new Color4(1, 0, 1, 1.0);
        particleSystem.color2 = new Color4(0.5, 0, 0.5, 1.0);
        particleSystem.minSize = 0.5;
        particleSystem.maxSize = 1.5;
        particleSystem.minLifeTime = 0.2;
        particleSystem.maxLifeTime = 0.5;
        particleSystem.emitRate = 1000;
        particleSystem.targetStopDuration = 0.1;
        particleSystem.start();

        if (this.context.audioManager) this.context.audioManager.playSound("explosion");
    }

    /**
     * Vérifie si cet ennemi peut être touché durant le dash actuel (anti double-hit)
     * @param {import("./Enemy.js").Enemy} enemy
     * @returns {boolean}
     */
    canHitEnemy(enemy) {
        if (this._recentlyHitEnemies.has(enemy)) return false;
        this._recentlyHitEnemies.add(enemy);
        return true;
    }

    /**
     * Soigne le joueur d'un montant donné (plafonné à maxHealth)
     * @param {number} amount
     */
    heal(amount) {
        const before = this.currentHealth;
        this.currentHealth = Math.min(this.currentHealth + amount, this.maxHealth);
        const gained = this.currentHealth - before;
        if (gained > 0) {
            logger.info("ENTITY", "DEBUG", `YASSO HEALED +${gained} HP (${this.currentHealth}/${this.maxHealth})`);
            this._showHealEffect();
        }
    }

    _showHealEffect() {
        const canvas = document.getElementById("renderCanvas");
        if (canvas) {
            canvas.style.filter = "brightness(1.4) saturate(2) hue-rotate(100deg)";
            setTimeout(() => { canvas.style.filter = ""; }, 200);
        }
        if (this.visualGroup) {
            this.visualGroup.scaling.setAll(1.45);
            setTimeout(() => {
                if (this.visualGroup) this.visualGroup.scaling.setAll(1.0);
            }, 250);
        }
    }

    _showPowerFlash(filterValue) {
        const canvas = document.getElementById("renderCanvas");
        if (!canvas) return;
        canvas.style.filter = filterValue;
        setTimeout(() => { canvas.style.filter = ""; }, 180);
    }


    /**
     * Augmente les HP max du joueur (récompense de fin d'étage).
     * Soigne aussi d'autant pour ne pas forcer à ramasser un bonus.
     * @param {number} amount
     */
    increaseMaxHealth(amount) {
        this.maxHealth += amount;
        this.currentHealth = Math.min(this.currentHealth + amount, this.maxHealth);
        logger.info("ENTITY", "DEBUG", `HP MAX +${amount} → ${this.maxHealth}`);
        this._showMaxHpIncreaseEffect();
    }

    /**
     * Effet visuel flash doré pour l'augmentation de HP max
     * @private
     */
    _showMaxHpIncreaseEffect() {
        const canvas = document.getElementById("renderCanvas");
        if (canvas) {
            canvas.style.filter = "brightness(1.6) saturate(2) hue-rotate(30deg)";
            setTimeout(() => { canvas.style.filter = ""; }, 300);
        }
        if (this.visualGroup) {
            this.visualGroup.scaling.setAll(1.6);
            setTimeout(() => {
                if (this.visualGroup) this.visualGroup.scaling.setAll(1.0);
            }, 320);
        }
    }

    /**
     * @typedef {Object} HealthData
     * @property {number} current
     * @property {number} max
     * @property {number} percentage
     */

    /**
     * Récupère les données de santé pour l'affichage
     * @returns {HealthData}
     */
    getHealthData() {
        return {
            current: this.currentHealth,
            max: this.maxHealth,
            percentage: (this.currentHealth / this.maxHealth) * 100
        };
    }

    /**
     * @typedef {Object} DashData
     * @property {boolean} isReady
     * @property {boolean} isDashing
     */

    /**
     * Récupère les données de dash pour l'affichage
     * @returns {DashData}
     */
    getDashData() {
        return {
            isReady: this.isDashReady,
            isDashing: this.isDashing,
            isSliding: this.isSliding,
            isSprinting: this.isSprinting,
            isJumping: this.isJumping,
            isJumpReady: this.isJumpReady,
            isSlideReady: this.isSlideReady,
        };
    }
}